import React, { useState, useEffect, useRef } from 'react';
import pb from '../services/pocketbase';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { nearbyCityIds } from '../services/cities';
import '../styles/Chats.css';

function ChatRoom() {
  const { chatId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { chatType, chatName } = location.state || {};
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuMsg, setMenuMsg] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);   // File объект
  const [imagePreview, setImagePreview] = useState(null);     // base64 для превью
  const [sending, setSending] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);       // полноэкранный просмотр
  const currentUser = pb.authStore.model;
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isGroup = chatType === 'group';

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const records = await pb.collection('messages').getList(1, 200, {
          filter: `chatId = "${chatId}"`,
          sort: 'created'
        });
        setMessages(records.items.map(formatMessage));
        markAsRead();
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadInitial();

    let unsubscribe = () => {};
    pb.collection('messages').subscribe(
      '*',
      (event) => {
        if (event.action !== 'create') return;
        if (event.record.chatId !== chatId) return;
        setMessages(prev => {
          if (prev.find(m => m.id === event.record.id)) return prev;
          return [...prev, formatMessage(event.record)];
        });
        markAsRead();
      },
      { filter: `chatId = "${chatId}"` }
    ).then(unsub => {
      unsubscribe = unsub;
    }).catch(e => console.error('Subscribe error:', e));

    return () => { unsubscribe(); };
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handler = () => setMenuMsg(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const markAsRead = async () => {
    if (!currentUser?.id) return;
    try {
      const unread = await pb.collection('messages').getFullList({
        filter: `chatId = "${chatId}" && userId != "${currentUser.id}" && read = false`,
        fields: 'id'
      });
      if (unread.length === 0) return;
      await Promise.all(unread.map(msg => pb.collection('messages').update(msg.id, { read: true })));
    } catch (e) {}
  };

  const formatMessage = (record) => ({
    id: record.id,
    collectionId: record.collectionId,
    userId: record.userId,
    userName: record.userName,
    text: record.text,
    image: record.image || null,
    time: new Date(record.created).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
    isOwn: record.userId === currentUser?.id
  });

  const getImageUrl = (msg) => {
    if (!msg.image) return null;
    return pb.files.getUrl({ id: msg.id, collectionId: msg.collectionId }, msg.image);
  };

  const handleImagePick = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    // Сбрасываем input чтобы можно было выбрать тот же файл повторно
    e.target.value = '';
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const sendPushNotification = async (playerIds, title, body) => {
    if (!playerIds || playerIds.length === 0) return;
    const appId = process.env.REACT_APP_ONESIGNAL_APP_ID;
    if (!appId) return;
    try {
      await fetch('https://onesignal.com/api/v1/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          app_id: appId,
          include_subscription_uuids: playerIds,
          headings: { ru: title, en: title },
          contents: { ru: body, en: body },
          url: 'https://awoo-v2.vercel.app/chats',
        }),
      });
    } catch (e) {
      console.warn('Push error:', e);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() && !selectedImage) return;
    setSending(true);
    const text = newMessage.trim();
    const imgFile = selectedImage;
    setNewMessage('');
    clearImage();
    try {
      const data = new FormData();
      data.append('chatId', chatId);
      data.append('userId', currentUser?.id);
      data.append('userName', currentUser?.name || 'Пользователь');
      data.append('text', text);
      data.append('chatType', chatType || 'group');
      if (imgFile) data.append('image', imgFile);
      await pb.collection('messages').create(data);

      // Push уведомления
      const senderName = currentUser?.name || 'Пользователь';
      const pushBody = text || '📷 Фото';
      if (isGroup) {
        const isCityChat = chatId.startsWith('city_');
        let playerIds = [];

        if (isCityChat) {
          // Городской чат — рассылаем всем у кого город в радиусе 100км
          const originCityId = chatId.replace('city_', '');
          const nearbyIds = nearbyCityIds(originCityId);
          const cityFilter = nearbyIds.map(id => `city = "${id}"`).join(' || ');
          const usersInArea = await pb.collection('users').getFullList({
            filter: `(${cityFilter}) && id != "${currentUser.id}"`,
            fields: 'oneSignalId'
          });
          playerIds = usersInArea.map(u => u.oneSignalId).filter(Boolean);
        } else {
          // Обычный групповой чат — по истории участников
          const allMsgs = await pb.collection('messages').getFullList({
            filter: `chatId = "${chatId}" && userId != "${currentUser.id}"`,
            fields: 'userId'
          });
          const uniqueUserIds = [...new Set(allMsgs.map(m => m.userId))];
          if (uniqueUserIds.length > 0) {
            const users = await pb.collection('users').getFullList({
              filter: uniqueUserIds.map(id => `id = "${id}"`).join(' || '),
              fields: 'oneSignalId'
            });
            playerIds = users.map(u => u.oneSignalId).filter(Boolean);
          }
        }

        await sendPushNotification(playerIds, `${chatName || 'Чат'}: ${senderName}`, pushBody);
      } else {
        // Приватный — получателю
        const otherUserId = chatId.split('_').find(id => id !== currentUser.id);
        if (otherUserId) {
          const otherUser = await pb.collection('users').getOne(otherUserId, { fields: 'oneSignalId' });
          if (otherUser?.oneSignalId) {
            await sendPushNotification([otherUser.oneSignalId], senderName, pushBody);
          }
        }
      }
    } catch (e) {
      console.error(e);
      setNewMessage(text);
      setSelectedImage(imgFile);
    } finally {
      setSending(false);
    }
  };

  const handleWritePrivate = (msg) => {
    setMenuMsg(null);
    const privateChatId = [currentUser.id, msg.userId].sort().join('_');
    navigate(`/chat/${privateChatId}`, {
      state: { chatType: 'private', chatName: msg.userName, otherUserId: msg.userId }
    });
  };

  const handleMsgClick = (e, msg) => {
    if (!isGroup || msg.isOwn) return;
    e.stopPropagation();
    setMenuMsg(menuMsg?.id === msg.id ? null : msg);
  };

  const canSend = (newMessage.trim() || selectedImage) && !sending;

  return (
    <div className="chat-room">
      <div className="chat-header">
        <div className="back-btn" onClick={() => navigate(-1)}>←</div>
        <div className="chat-header-info">
          <div className="chat-header-name">{chatName || 'Чат'}</div>
          {isGroup && <div className="chat-header-desc">Групповой чат</div>}
        </div>
        <div className="chat-header-actions"><span>⋮</span></div>
      </div>

      <div className="messages-container">
        {loading ? (
          <div style={{textAlign:'center', padding:'20px', color:'#999'}}>Загрузка...</div>
        ) : messages.length === 0 ? (
          <div style={{textAlign:'center', padding:'40px 20px', color:'#999'}}>
            <div style={{fontSize:'40px'}}>💬</div>
            <p>Нет сообщений. Напишите первым!</p>
          </div>
        ) : (
          messages.map(msg => {
            const imgUrl = getImageUrl(msg);
            return (
              <div key={msg.id} className={`message ${msg.isOwn ? 'own' : 'other'}`}
                style={{position:'relative'}}
                onClick={(e) => handleMsgClick(e, msg)}
              >
                {!msg.isOwn && (
                  <div className="message-avatar" style={{cursor: isGroup ? 'pointer' : 'default'}}>👤</div>
                )}
                <div className="message-bubble" style={{cursor: isGroup && !msg.isOwn ? 'pointer' : 'default'}}>
                  {!msg.isOwn && (
                    <div className="message-author" style={{display:'flex', alignItems:'center', gap:'6px'}}>
                      {msg.userName}
                      {isGroup && <span style={{fontSize:'10px', color:'#aaa'}}>• нажми для ЛС</span>}
                    </div>
                  )}

                  {/* Фото в сообщении */}
                  {imgUrl && (
                    <img
                      src={imgUrl}
                      alt="фото"
                      onClick={(e) => { e.stopPropagation(); setLightboxImg(imgUrl); }}
                      style={{
                        display: 'block',
                        maxWidth: '220px',
                        maxHeight: '220px',
                        width: '100%',
                        borderRadius: '10px',
                        marginBottom: msg.text ? '6px' : '0',
                        cursor: 'zoom-in',
                        objectFit: 'cover',
                      }}
                    />
                  )}

                  {msg.text && <div className="message-text">{msg.text}</div>}
                  <div className="message-time">{msg.time}</div>
                </div>

                {/* Меню при нажатии */}
                {menuMsg?.id === msg.id && (
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      position:'absolute', bottom:'100%',
                      left: msg.isOwn ? 'auto' : '48px',
                      right: msg.isOwn ? '0' : 'auto',
                      background:'white', borderRadius:'12px',
                      boxShadow:'0 4px 20px rgba(0,0,0,0.15)',
                      padding:'8px', zIndex:100, minWidth:'180px', marginBottom:'4px'
                    }}
                  >
                    <div style={{fontSize:'12px', color:'#999', padding:'4px 8px 6px', fontWeight:'600'}}>
                      {msg.userName}
                    </div>
                    <div
                      onClick={() => handleWritePrivate(msg)}
                      style={{padding:'10px 12px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', display:'flex', alignItems:'center', gap:'8px', color:'#3B5998', fontWeight:'600'}}
                      onMouseEnter={e => e.currentTarget.style.background='#F5F9FF'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    >
                      💬 Написать в личку
                    </div>
                    <div
                      onClick={() => navigate(`/profile/${msg.userId}`)}
                      style={{padding:'10px 12px', borderRadius:'8px', cursor:'pointer', fontSize:'14px', display:'flex', alignItems:'center', gap:'8px', color:'#555'}}
                      onMouseEnter={e => e.currentTarget.style.background='#F5F5F5'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    >
                      👤 Открыть профиль
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Превью выбранного фото */}
      {imagePreview && (
        <div style={{
          padding: '8px 16px',
          background: 'white',
          borderTop: '1px solid #EEE',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <img src={imagePreview} alt="превью" style={{width:'60px', height:'60px', borderRadius:'8px', objectFit:'cover'}} />
          <div style={{flex:1, fontSize:'13px', color:'#555'}}>Фото готово к отправке</div>
          <button
            onClick={clearImage}
            style={{background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#999', lineHeight:1}}
          >✕</button>
        </div>
      )}

      {/* Поле ввода */}
      <div className="message-input-container">
        {/* Скрытый input для выбора файла */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{display:'none'}}
          onChange={handleImagePick}
        />
        {/* Кнопка прикрепить фото */}
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: '45px', height: '45px', borderRadius: '50%',
            background: selectedImage ? 'linear-gradient(135deg, #3B5998, #6BA3E8)' : '#F0F0F0',
            border: 'none', fontSize: '20px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, transition: 'background 0.2s',
          }}
          title="Прикрепить фото"
        >
          📎
        </button>
        <input
          type="text"
          className="message-input"
          placeholder="Написать сообщение..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && canSend && handleSend()}
        />
        <button className="send-btn" onClick={handleSend} disabled={!canSend}>
          {sending ? '⏳' : '➤'}
        </button>
      </div>

      {/* Лайтбокс — полноэкранный просмотр фото */}
      {lightboxImg && (
        <div
          onClick={() => setLightboxImg(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightboxImg}
            alt="фото"
            style={{maxWidth: '95vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain'}}
          />
          <button
            onClick={() => setLightboxImg(null)}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'rgba(255,255,255,0.15)', border: 'none',
              color: 'white', fontSize: '24px', width: '40px', height: '40px',
              borderRadius: '50%', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>
      )}
    </div>
  );
}

export default ChatRoom;
